import { computed, inject, Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { ENVIRONMENT } from '@environment';
import { LocationService } from '@usheru-hff/sdk/location';
import { MovieService } from '@usheru-hff/sdk/movie';
import {
  Movie,
  MoviesFeedStateInterface,
  MovieCardConfigInterface,
  MovieListWrapper,
  PageType
} from '@usheru-hff/shared/domain';
import { BaseFeedService, FeedConfigType, PaginationStore } from '@usheru-hff/ui/feed';

interface ExtraArgs {
  type?: PageType;
}

@Injectable()
export class MoviesFeedService implements BaseFeedService<Movie, MovieListWrapper, ExtraArgs> {
  private readonly movieService = inject(MovieService);
  private readonly locationService = inject(LocationService);
  private readonly pagination = inject(PaginationStore);
  private env = inject(ENVIRONMENT);

  readonly movieClikedEvent = new Subject<Movie>();
  readonly noMoviesFoundEvent = new Subject<void>();
  readonly loadingEvent = new Subject<boolean>();
  readonly page = computed(() => this.pagination.page());
  // state
  state = signal<MoviesFeedStateInterface>({
    type: 'all',
    filters: undefined,
    items: undefined,
    movieCardConfig: undefined,
    feedConfig: 'grid',
    loading: false,
    movieCardIndexFlipped: undefined
  });

  initFeed(filters: MovieListWrapper = {}, page: number = 1, extraArgs?: ExtraArgs) {
    const { type = 'all' } = extraArgs || {};
    this.state.update(state => ({
      ...state,
      type,
      filters
    }));
    this.pagination.setPage(page);
  }

  updateType(type: PageType): void {
    this.state.update(state => ({
      ...state,
      type
    }));
  }

  updateFilters(filters: MovieListWrapper | undefined): void {
    this.state.update(state => ({
      ...state,
      filters
    }));

    if (filters) {
      const { page = 1 } = filters;
      this.updatePageNumber(page);
    }
  }
  updateMovieCardConfig(movieCardConfig: MovieCardConfigInterface): void {
    this.state.update(state => ({
      ...state,
      movieCardConfig
    }));
  }
  updateFeedConfig(feedConfig: FeedConfigType): void {
    this.state.update(state => ({
      ...state,
      feedConfig
    }));
  }

  updatePageNumber(page: number = 1): void {
    this.pagination.setPage(page);
  }

  updateItems(movies: Movie[], concatenate: boolean = false): void {
    if (!movies || movies.length === 0) this.noMoviesFoundEvent.next();

    this.state.update(state => ({
      ...state,
      items: concatenate ? [...(state.items ?? []), ...movies] : movies
    }));
  }

  clearPagination() {
    this.pagination.reset();
  }

  setLoading(loading: boolean): void {
    this.loadingEvent.next(loading);
    this.state.update(state => ({
      ...state,
      loading
    }));
  }
  // end state

  getItems(filters: MovieListWrapper | undefined, extrArgs?: ExtraArgs): Observable<Movie[]> {
    const country = this.env.countryBasedApplication?.enabled ? this.locationService.selectedCountry : undefined;
    const type = extrArgs?.type || this.state().type;

    return this.movieService.getMovies(type, filters, country);
  }
}
